/* A line's work (Duncan 2026-10-23): what a sub does on E3 Subs' line page, a PM does here -- "marking it is
   finished, before, and after pictures and such ... without having to go to the subs section". E3 Subs keeps all of
   it; Summit's routes/taskWork.js pass these actions on. The words are the ones agreed with E3 Subs
   (SUMMIT-LINES-API.md), so both apps say the same thing. */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../ui';
import { ErrorBanner, PrimaryButton, SecondaryButton } from './SubUi';
import {
  completeWorkLine, uncompleteWorkLine, addWorkPhoto, removeWorkPhoto, raiseWorkIssue, answerWorkIssue, recordSpotCheck, verifyPunch,
  addWorkNote, setWorkInstalled,
} from '../../api';
import { photoBody, useConverting } from '../../lib/photos';

// Raise an issue's kinds (2026-10-24, as E3 Subs): material has its own way in (the sub's Order material), and so have
// Can't get in and a count that differs -- E3 Subs' line page sends those; here they're read and answered.
export const ISSUE_TYPES = [['FIXTURE_CHANGE', 'Fixture change'], ['FIELD_PURCHASE', 'Field purchase'], ['QUESTION', 'Question'], ['OTHER', 'Other']];
const ISSUE_LABEL = { MATERIAL: 'Material needed', FIXTURE_CHANGE: 'Fixture change', FIELD_PURCHASE: 'Field purchase', QUESTION: 'Question',
  OTHER: 'Other', ACCESS: 'Can’t get in', COUNT: 'Count is different' };
export const issueTypeOf = (k) => ISSUE_LABEL[k] || k;
const ACCESS_REASON = { LOCKED: 'Locked', OCCUPIED: 'Occupied', NO_ESCORT: 'No escort', OTHER: 'Other' };
export const FINDING_TYPES = [['COUNT', 'Count differs'], ['DIRTY_LENS', 'Dirty lens'], ['DAMAGE', 'Damage'], ['OTHER', 'Other']];
export const findingTypeOf = (k) => (FINDING_TYPES.find(([v]) => v === k) || [k, k])[1];
// The punch list's words (2026-10-26): what the sub marks is "Done" (E3 Subs' lines.get still says FIXED)
const PUNCH = { OPEN: 'Open', FIXED: 'Done', VERIFIED: 'Verified' };
/** "Jordan Reyes" for a sub's person, "Duncan Broach (E3)" for E3's. */
export const whoOf = (p) => (p && p.name ? `${p.name}${p.by === 'e3' ? ' (E3)' : ''}` : '');
export const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
/** "Nov 3" from "2026-11-03" -- a calendar day, read as that day here (never UTC midnight). */
const dayShort = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
};
const todayYmd = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
/** A line's work as E3 Subs keeps it, or none yet. */
export const EMPTY_WORK = { done: false, doneAt: null, doneBy: null, installers: [], installed: 0, photos: { before: [], after: [] }, issues: [], notes: [], spotCheck: null };
export const newRequestId = () => (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => { const r = (Math.random() * 16) | 0; return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16); }));
const LAST_INSTALLERS = 'summit.lastInstallers'; // this browser's last initials, offered next time
const lastInstallers = () => { try { return JSON.parse(localStorage.getItem(LAST_INSTALLERS) || '[]').filter((x) => /^[A-Z]{1,6}$/.test(x)).slice(0, 6); } catch (e) { return []; } };

const muted = { fontSize: 12.5, color: 'var(--text-3)' };
const linkBtn = { border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--accent-text)', fontFamily: 'inherit', whiteSpace: 'nowrap' };
const smallBtn = { height: 28, padding: '0 10px', fontSize: 13 };
const pill = (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: bg, color: fg, whiteSpace: 'nowrap' });
const box = { padding: '8px 10px', borderRadius: 9, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }; // white, on the soft cards
const input = { width: '100%', height: 32, padding: '0 9px', borderRadius: 7, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-1)', fontSize: 13.5, fontFamily: 'inherit', boxSizing: 'border-box' };
const textBox = { ...input, height: 'auto', minHeight: 54, padding: '7px 9px', lineHeight: 1.4, resize: 'vertical' };
const choiceGroup = { display: 'inline-flex', flexWrap: 'wrap', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 7, padding: 2, gap: 2 };
const choiceBtn = { border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--text-3)', fontWeight: 500, fontSize: 13, padding: '4px 10px', borderRadius: 5, fontFamily: 'inherit' };
const choiceOn = { background: 'var(--bg-surface)', color: 'var(--text-1)', boxShadow: 'var(--shadow-xs)' };
export const greenPill = pill('var(--success-soft)', 'var(--success)');
export const amberPill = pill('var(--warning-soft)', 'var(--warning)');
export const redPill = pill('var(--danger-soft)', 'var(--danger)');
export const bluePill = pill('var(--accent-soft)', 'var(--accent-text)');
export const grayPill = pill('var(--bg-surface-2)', 'var(--text-3)');
// E3 Subs' blue (Count differs, Ordered): Summit's accent is sage, so its Labor blue.
const infoPill = pill('color-mix(in oklch, var(--type-labor) 15%, transparent)', 'var(--type-labor)');

/** What's still open on a line: requests not resolved, material not delivered (as E3 Subs counts them). */
export const openRequests = (w) => ((w && w.issues) || []).filter((i) => i.status !== 'RESOLVED' && i.status !== 'DELIVERED');

/** A line's pills, in E3 Subs' words and order (2026-10-24): Partly done · 2/5, Waiting on material, Can't get in,
    Count differs, Issue, To fix. (To walk shows with the line's state.) */
export function linePills(w, qty) {
  const open = openRequests(w);
  const has = (types) => open.some((i) => types.includes(i.type));
  const out = [];
  if (!w.done && (w.installed || 0) > 0) out.push([`Partly done · ${w.installed}/${qty || 0}`, amberPill]);
  if (has(['MATERIAL'])) out.push(['Waiting on material', amberPill]);
  if (has(['ACCESS'])) out.push(['Can’t get in', redPill]);
  if (has(['COUNT'])) out.push(['Count differs', infoPill]);
  if (has(['FIXTURE_CHANGE', 'FIELD_PURCHASE', 'QUESTION', 'OTHER'])) out.push(['Issue', amberPill]);
  const sc = w.spotCheck;
  if (sc && sc.status === 'FINDINGS' && (sc.findings || []).some((f) => !f.punch || f.punch.status === 'OPEN')) out.push(['To fix', redPill]);
  return out;
}

/** A request's state in E3's words: material Requested / Acknowledged / Ordered · expected Nov 3 / Delivered /
    Resolved; the rest Open (red for Can't get in) / Acknowledged / Resolved. */
function requestState(i) {
  if (i.type === 'MATERIAL') {
    if (i.status === 'DELIVERED') return ['Delivered', greenPill];
    if (i.status === 'ORDERED') {
      const when = i.material && dayShort(i.material.expectedAt);
      return [when ? `Ordered · expected ${when}` : 'Ordered', infoPill];
    }
    if (i.status === 'RESOLVED') return ['Resolved', grayPill];
    if (i.status === 'ACKNOWLEDGED') return ['Acknowledged', amberPill];
    return ['Requested', amberPill];
  }
  if (i.status === 'RESOLVED') return ['Resolved', grayPill];
  if (i.status === 'ACKNOWLEDGED') return ['Acknowledged', amberPill];
  return ['Open', i.type === 'ACCESS' ? redPill : amberPill];
}

/** "Material · 2 × 24DOORLED30 (A2)", "Can't get in · Locked", "Count is different · counted 14, the workbook says 16". */
function requestTitle(i) {
  if (i.type === 'MATERIAL' && i.material && i.material.item) return `Material · ${i.material.quantity || 1} × ${i.material.item}`; // words-only ones: Material needed
  if (i.type === 'ACCESS') return `Can’t get in${i.access && ACCESS_REASON[i.access.reason] ? ` · ${ACCESS_REASON[i.access.reason]}` : ''}`;
  if (i.type === 'COUNT' && i.count && i.count.counted != null) return `Count is different · counted ${i.count.counted}, the workbook says ${i.count.expected}`;
  return issueTypeOf(i.type);
}
const REQUEST_ICON = { MATERIAL: 'inbox', ACCESS: 'lock', COUNT: 'list' };

/** A choice of a few, as the segmented controls elsewhere. */
function Choice({ label, options, value, onChange }) {
  return (
    <div role="radiogroup" aria-label={label} style={choiceGroup}>
      {options.map(([v, l]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)} style={{ ...choiceBtn, ...(value === v ? choiceOn : {}) }}>{l}</button>
      ))}
    </div>
  );
}

/** A photo, small; it opens big. A broken link (they last about 15 minutes) asks for fresh ones. */
export function Thumb({ photo, size = 64, onOpen, onRemove, onExpired }) {
  return (
    <span className="lw-thumb" style={{ position: 'relative', display: 'inline-block', width: size, height: size }}>
      <button type="button" onClick={onOpen} title={`${photo.kind === 'after' ? 'After' : photo.kind === 'before' ? 'Before' : 'Photo'}${photo.takenBy ? ` · ${whoOf(photo.takenBy)}` : ''}${photo.takenAt ? ` · ${fmtWhen(photo.takenAt)}` : ''}`}
        style={{ width: size, height: size, padding: 0, border: '1px solid var(--border-default)', borderRadius: 9, overflow: 'hidden', cursor: 'zoom-in', background: 'var(--bg-surface)', display: 'block' }}>
        <img src={photo.thumbUrl} alt="" onError={onExpired} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </button>
      {onRemove && (
        <button type="button" className="lw-remove" onClick={onRemove} aria-label="Remove this photo" title="Remove this photo"
          style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: 999, border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--danger)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
          <Icon name="trash" size={11} />
        </button>
      )}
    </span>
  );
}

/** A button that picks photos (a phone opens its camera or library); they go one at a time. As a tile (64 px, a
    camera, "Add") beside a strip's photos; amber when it's the photo the line still needs. */
export function AddPhoto({ label, busy, onFiles, compact, need, size = 64 }) {
  const ref = useRef(null);
  const converting = useConverting(); // a HEIC photo being turned into a JPEG first (lib/photos)
  const tone = need ? 'var(--warning)' : 'var(--accent-text)';
  return (
    <>
      <input ref={ref} type="file" accept="image/*" multiple style={{ display: 'none' }}
        onChange={(e) => { const files = [...(e.target.files || [])]; e.target.value = ''; if (files.length) onFiles(files); }} />
      <button type="button" onClick={() => ref.current && ref.current.click()} disabled={busy} aria-label={label} title={label}
        style={compact ? {
          height: 32, padding: '0 12px', borderRadius: 7, border: '1px dashed var(--border-strong)', background: 'transparent', color: tone,
          cursor: busy ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: busy ? 0.6 : 1,
        } : {
          width: size, height: size, padding: 0, borderRadius: 9, border: `1.5px dashed ${need ? 'var(--warning)' : 'var(--border-strong)'}`,
          background: need ? 'var(--warning-soft)' : 'transparent', color: tone, cursor: busy ? 'default' : 'pointer', fontSize: 11.5, fontWeight: 600,
          fontFamily: 'inherit', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, opacity: busy ? 0.6 : 1,
        }}>
        <Icon name={busy ? 'refresh-cw' : compact ? 'plus' : 'camera'} size={compact ? 13 : 17} />{busy ? (converting ? 'Converting…' : 'Sending…') : compact ? label : 'Add'}
      </button>
    </>
  );
}

/** One of the three things a line needs (2026-10-24, Duncan: no numbered steps, no text explaining them): its
    label -- ticked once it's in, amber while it's still needed -- and what you do, right in it. */
function WorkBox({ label, state, children }) {
  const need = state === 'need';
  const done = state === 'done';
  return (
    <div style={{ minWidth: 0, padding: '8px 10px', borderRadius: 10, background: need ? 'var(--warning-soft)' : 'var(--bg-surface)',
      border: need ? '1.5px dashed var(--warning)' : '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
        color: done ? 'var(--success)' : need ? 'var(--warning)' : 'var(--text-3)', marginBottom: 6 }}>
        {done && <Icon name="check" size={11} stroke={2.4} />}{label}
      </div>
      {children}
    </div>
  );
}
const softCard = { background: 'var(--bg-surface-2)', borderRadius: 12, padding: '12px 14px', minWidth: 0 };
const cardHead = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 650, color: 'var(--text-1)', margin: '0 0 10px' };

/** A photo shown big, with the others of its strip a click away. Esc closes it alone, not the window. */
export function PhotoViewer({ list, index, onIndex, onClose }) {
  const p = list[index];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowRight' && index < list.length - 1) onIndex(index + 1);
      else if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
    };
    window.addEventListener('keydown', onKey, true); // first, so the window under it doesn't close too
    return () => window.removeEventListener('keydown', onKey, true);
  }, [index, list.length, onClose, onIndex]);
  if (!p) return null;
  const nav = { width: 40, height: 40, borderRadius: 999, border: 'none', background: 'rgba(255,255,255,0.14)', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Photo" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 10050, background: 'rgba(12,12,10,0.86)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 20 }}>
      <img src={p.viewUrl} alt="" style={{ maxWidth: '92vw', maxHeight: '78vh', borderRadius: 8, boxShadow: '0 20px 60px rgba(0,0,0,0.45)' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, color: '#fff', fontSize: 13.5 }}>
        <button type="button" aria-label="Previous photo" disabled={index === 0} onClick={() => onIndex(index - 1)} style={{ ...nav, opacity: index === 0 ? 0.3 : 1 }}><Icon name="arrow-left" size={16} /></button>
        <span>{p.kind === 'after' ? 'After' : p.kind === 'before' ? 'Before' : 'Photo'}{p.takenBy ? ` · ${whoOf(p.takenBy)}` : ''}{p.takenAt ? ` · ${fmtWhen(p.takenAt)}` : ''} · {index + 1} of {list.length}</span>
        <button type="button" aria-label="Next photo" disabled={index >= list.length - 1} onClick={() => onIndex(index + 1)} style={{ ...nav, opacity: index >= list.length - 1 ? 0.3 : 1, transform: 'scaleX(-1)' }}><Icon name="arrow-left" size={16} /></button>
        <button type="button" onClick={onClose} style={{ ...nav, width: 'auto', padding: '0 14px', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>Close</button>
      </div>
    </div>,
    document.body,
  );
}

/** Initials, as chips: type them, Enter (or a space or comma) adds; up to 6 people of 1-6 letters. */
function InstallersInput({ value, onChange }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim().toUpperCase();
    if (/^[A-Z]{1,6}$/.test(v) && !value.includes(v) && value.length < 6) onChange([...value, v]);
    setDraft('');
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {value.map((x) => (
        <span key={x} style={{ ...pill('var(--bg-surface)', 'var(--text-1)'), border: '1px solid var(--border-default)' }}>
          {x}
          <button type="button" aria-label={`Remove ${x}`} onClick={() => onChange(value.filter((y) => y !== x))}
            style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: 'var(--text-3)', fontSize: 14, lineHeight: 1 }}>×</button>
        </span>
      ))}
      {value.length < 6 && (
        <input value={draft} aria-label="Add an installer’s initials" placeholder={value.length ? 'Add initials' : 'Initials, e.g. JR'}
          onChange={(e) => setDraft(e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 6).toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); add(); }
            else if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
          }}
          onBlur={add}
          style={{ ...input, width: 120, height: 28 }} />
      )}
    </div>
  );
}

/** One request on a line (2026-10-24): what the sub asked (material, can't get in, a count that differs, an issue),
    how it stands in E3's words, and E3's answers -- material Mark ordered (with the day it's expected), Mark
    delivered; anything Acknowledge and Resolve. */
export function RequestCard({ issue, busy, onAnswer, onOpenPhotos, onExpired }) {
  const [mode, setMode] = useState(null); // 'order' (the expected day) or 'resolve' (what was done)
  const [note, setNote] = useState('');
  const [day, setDay] = useState('');
  const material = issue.type === 'MATERIAL';
  const closed = issue.status === 'RESOLVED' || issue.status === 'DELIVERED';
  const [stateText, stateStyle] = requestState(issue);
  const details = [material && issue.material && issue.material.neededBy ? `Needed by ${dayShort(issue.material.neededBy)}` : null, issue.note || null].filter(Boolean);
  const answerWord = { ACKNOWLEDGED: 'Acknowledged', RESOLVED: 'Resolved', ORDERED: 'Ordered', DELIVERED: 'Delivered' }[issue.status];
  const order = () => { setDay((issue.material && issue.material.expectedAt) || ''); setMode('order'); };
  const goodDay = /^\d{4}-\d{2}-\d{2}$/.test(day);
  return (
    <div style={{ ...box, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span aria-hidden="true" style={{ color: 'var(--text-3)', paddingTop: 1 }}><Icon name={REQUEST_ICON[issue.type] || 'alert-triangle'} size={15} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5, fontWeight: 650, color: closed ? 'var(--text-3)' : 'var(--text-1)', flex: '1 1 150px', minWidth: 0 }}>{requestTitle(issue)}</span>
            <span style={stateStyle}>{stateText}</span>
          </div>
          {details.length > 0 && <div style={{ fontSize: 13.5, color: 'var(--text-1)', marginTop: 3, whiteSpace: 'pre-wrap' }}>{details.join(' · ')}</div>}
          <div style={{ ...muted, marginTop: 3 }}>
            {whoOf(issue.raisedBy)}{issue.raisedAt ? `, ${fmtWhen(issue.raisedAt)}` : ''}
            {issue.answeredAt && answerWord ? ` · ${answerWord} by ${whoOf(issue.answeredBy)}, ${fmtWhen(issue.answeredAt)}` : ''}
          </div>
          {issue.resolution ? <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 3, whiteSpace: 'pre-wrap' }}>{issue.resolution}</div> : null}
          {issue.photos && issue.photos.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              {issue.photos.map((p, k) => <Thumb key={p.id} photo={p} size={44} onOpen={() => onOpenPhotos(issue.photos, k)} onExpired={onExpired} />)}
            </div>
          )}
          {!closed && !mode && (
            <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
              {material && issue.status !== 'ORDERED' && <button type="button" disabled={!!busy} onClick={order} style={linkBtn}>Mark ordered</button>}
              {material && issue.status === 'ORDERED' && <button type="button" disabled={!!busy} onClick={() => onAnswer('DELIVERED')} style={linkBtn}>Mark delivered</button>}
              {material && issue.status === 'ORDERED' && issue.material && <button type="button" disabled={!!busy} onClick={order} style={linkBtn}>Change the day</button>}
              {issue.status === 'OPEN' && <button type="button" disabled={!!busy} onClick={() => onAnswer('ACKNOWLEDGED')} style={linkBtn}>Acknowledge</button>}
              <button type="button" disabled={!!busy} onClick={() => setMode('resolve')} style={linkBtn}>Resolve</button>
            </div>
          )}
          {!closed && mode === 'order' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ ...muted, color: 'var(--text-2)' }}>Expected
                <input type="date" value={day} min={todayYmd()} autoFocus onChange={(e) => setDay(e.target.value)} aria-label="The day it's expected"
                  style={{ ...input, width: 150, height: 28, marginLeft: 8 }} />
              </label>
              <PrimaryButton busy={busy === 'answer'} disabled={!goodDay} onClick={async () => { if (await onAnswer('ORDERED', '', day)) setMode(null); }} style={smallBtn}>
                {issue.status === 'ORDERED' ? 'Save the day' : 'Mark ordered'}
              </PrimaryButton>
              <SecondaryButton onClick={() => setMode(null)} style={smallBtn}>Cancel</SecondaryButton>
            </div>
          )}
          {!closed && mode === 'resolve' && (
            <div style={{ marginTop: 8 }}>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} autoFocus placeholder="What was done (optional)" aria-label="What was done" style={textBox} />
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <PrimaryButton busy={busy === 'answer'} onClick={async () => { if (await onAnswer('RESOLVED', note)) { setMode(null); setNote(''); } }} style={smallBtn}>Resolve</PrimaryButton>
                <SecondaryButton onClick={() => { setMode(null); setNote(''); }} style={smallBtn}>Cancel</SecondaryButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** A note on a line: the sub's (it emailed the job's PMs once) or E3's (it emails nobody). */
export function NoteCard({ note }) {
  return (
    <div style={{ ...box, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span aria-hidden="true" style={{ color: 'var(--text-3)', paddingTop: 1 }}><Icon name="pencil" size={15} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 650, color: 'var(--text-1)' }}>Note</div>
          <div style={{ fontSize: 13.5, color: 'var(--text-1)', marginTop: 3, whiteSpace: 'pre-wrap' }}>{note.body}</div>
          <div style={{ ...muted, marginTop: 3 }}>{whoOf(note.by)}{note.at ? `, ${fmtWhen(note.at)}` : ''}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * The line's work: its before and after photos; Mark complete with its installers (after an after photo); its
 * issues; its spot check when it's picked. `work` is E3 Subs' state for the line (EMPTY_WORK when there's none yet);
 * each action's answer comes back through onChange(line state).
 */
export default function LineWork({ line, work, onChange, onDirty, confirm, onExpired }) {
  const w = work || EMPTY_WORK;
  const before = (w.photos && w.photos.before) || [];
  const after = (w.photos && w.photos.after) || [];
  const [busy, setBusy] = useState(null); // the action running: 'before', 'after', 'complete', 'issue', 'answer', 'walk', 'note', 'partly'
  const [error, setError] = useState(null);
  const [installers, setInstallers] = useState(() => (w.installers && w.installers.length ? w.installers : lastInstallers()));
  const [form, setForm] = useState(null); // the Requests and notes form open: 'note', 'issue' or 'partly' -- one at a time
  const [issue, setIssue] = useState({ type: '', note: '', photoIds: [], photos: [] });
  const [noteDraft, setNoteDraft] = useState('');
  const [partlyDraft, setPartlyDraft] = useState('');
  const [walk, setWalk] = useState(null); // the findings being written: [{ type, note, photoIds, photos }]
  const [viewing, setViewing] = useState(null); // { list, index }
  const qty = Number(line.pro_qty) || 0;
  const installedNow = Number(w.installed) || 0;
  const dirty = (form === 'issue' && (issue.note.trim() || issue.photos.length)) || (form === 'note' && noteDraft.trim())
    || (form === 'partly' && partlyDraft !== '' && Number(partlyDraft) !== installedNow)
    || (walk && walk.some((f) => f.note.trim() || f.photos.length));
  useEffect(() => { onDirty(!!dirty); }, [dirty, onDirty]);

  const run = async (key, fn) => {
    setBusy(key);
    setError(null);
    try {
      const r = await fn();
      if (r && r.line) onChange(r.line);
      return r;
    } catch (e) {
      setError(e.message || 'That didn’t go through');
      return null;
    } finally {
      setBusy(null);
    }
  };
  const sendPhotos = async (kind, files, key = kind) => {
    const sent = [];
    for (const f of files) {
      const r = await run(key, async () => addWorkPhoto(line.id, { kind, ...(await photoBody(f)), requestId: newRequestId() }));
      if (!r) break;
      if (r.photo) sent.push(r.photo);
    }
    return sent;
  };
  const removePhoto = async (p) => {
    const ok = await confirm({ title: 'Remove this photo?', message: `The ${p.kind} photo comes off the line on E3 Subs. It’s kept there 30 days for the record.`, confirmLabel: 'Remove', danger: true });
    if (ok) await run(p.kind, () => removeWorkPhoto(line.id, p.id, { requestId: newRequestId() }));
  };
  const complete = async () => {
    const r = await run('complete', () => completeWorkLine(line.id, { installers, requestId: newRequestId() }));
    if (r) { try { localStorage.setItem(LAST_INSTALLERS, JSON.stringify(installers)); } catch (e) { /* private window */ } }
  };
  const undo = async () => {
    const ok = await confirm({ title: 'Undo complete?', message: 'The line goes back to not complete. Its photos stay.', confirmLabel: 'Undo complete' });
    if (ok) await run('complete', () => uncompleteWorkLine(line.id, { requestId: newRequestId() }));
  };
  const closeForm = () => { setForm(null); setIssue({ type: '', note: '', photoIds: [], photos: [] }); setNoteDraft(''); setPartlyDraft(''); };
  const openForm = (f) => {
    if (form === f) { closeForm(); return; }
    closeForm();
    if (f === 'partly') setPartlyDraft(installedNow ? String(installedNow) : '');
    setForm(f);
  };
  const raise = async () => {
    const r = await run('issue', () => raiseWorkIssue(line.id, { type: issue.type, note: issue.note, photoIds: issue.photoIds, requestId: newRequestId() }));
    if (r) closeForm();
  };
  const addNote = async () => {
    const r = await run('note', () => addWorkNote(line.id, { body: noteDraft.trim(), requestId: newRequestId() }));
    if (r) closeForm();
  };
  // Partly done: 0 to one under the line's quantity (all of them = Mark complete); empty or 0 clears it.
  const partlyN = partlyDraft === '' ? 0 : Number(partlyDraft);
  const partlyOk = Number.isInteger(partlyN) && partlyN >= 0 && partlyN < qty;
  const saveInstalled = async () => {
    const r = await run('partly', () => setWorkInstalled(line.id, { installed: partlyN, requestId: newRequestId() }));
    if (r) closeForm();
  };
  const answer = (iss, status, note, expectedAt) => run('answer', () => answerWorkIssue(line.id, iss.id,
    { status, ...(note ? { note } : {}), ...(expectedAt ? { expectedAt } : {}), requestId: newRequestId() }));
  const recordWalk = async (result) => {
    const findings = result === 'FINDINGS' ? walk.map((f) => ({ type: f.type, note: f.note, photoIds: f.photoIds })) : undefined;
    const r = await run('walk', () => recordSpotCheck(line.id, { result, findings, requestId: newRequestId() }));
    if (r) setWalk(null);
  };
  const openPhotos = (list, index) => setViewing({ list, index });
  const sc = w.spotCheck;
  const issues = w.issues || [];
  const openCount = openRequests(w).length;
  // Requests and notes: one list, newest first (as E3 Subs' line page).
  const items = [
    ...issues.map((i) => ({ at: i.raisedAt || '', key: `i${i.id}`, issue: i })),
    ...(w.notes || []).map((n) => ({ at: n.at || '', key: `n${n.id}`, note: n })),
  ].sort((a, b) => String(b.at).localeCompare(String(a.at)));
  const canPartly = qty > 0 && !w.done;
  const walkReady = walk && walk.length > 0 && walk.every((f) => f.type && f.note.trim());

  const hasAfter = after.length > 0;
  const doneInstallers = w.installers || [];
  const boxes = (
    <div className="lw-boxes" style={{ display: 'grid', gridTemplateColumns: w.done ? 'repeat(3, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr)) auto', gap: 10, alignItems: 'stretch' }}>
      {[['before', 'Before', before], ['after', 'After', after]].map(([kind, label, list]) => (
        <WorkBox key={kind} label={label} state={list.length ? 'done' : kind === 'after' && !w.done ? 'need' : 'todo'}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {list.map((p, k) => <Thumb key={p.id} photo={p} size={52} onOpen={() => openPhotos(list, k)} onRemove={() => removePhoto(p)} onExpired={onExpired} />)}
            <AddPhoto size={52} label={`Add ${kind} photo`} busy={busy === kind} need={kind === 'after' && !list.length && !w.done} onFiles={(files) => sendPhotos(kind, files)} />
          </div>
        </WorkBox>
      ))}
      <WorkBox label="Installers" state={w.done ? (doneInstallers.length ? 'done' : 'todo') : installers.length ? 'done' : 'need'}>
        {w.done ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {doneInstallers.map((x) => <span key={x} style={{ ...pill('var(--bg-surface)', 'var(--text-1)'), border: '1px solid var(--border-default)' }}>{x}</span>)}
          </div>
        ) : <InstallersInput value={installers} onChange={setInstallers} />}
      </WorkBox>
      {!w.done && (
        <PrimaryButton busy={busy === 'complete'} disabled={!hasAfter || !installers.length} onClick={complete} style={{ alignSelf: 'center' }}>
          <Icon name="check" size={13} />Mark complete
        </PrimaryButton>
      )}
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {w.done ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px 10px', flexWrap: 'wrap', padding: '10px 12px', borderRadius: 10, background: 'var(--success-soft)' }}>
          <span style={greenPill}><Icon name="check" size={12} />Complete</span>
          <span style={{ ...muted, color: 'var(--text-2)' }}>by {whoOf(w.doneBy)}{w.doneAt ? `, ${fmtWhen(w.doneAt)}` : ''} · Installers <b style={{ color: 'var(--text-1)' }}>{(w.installers || []).join(', ') || '—'}</b></span>
          <span style={{ marginLeft: 'auto' }}><SecondaryButton busy={busy === 'complete'} onClick={undo} style={smallBtn}>Undo complete</SecondaryButton></span>
        </div>
      ) : null}
      {boxes}
      {sc && (
      <div style={softCard}>
        <p style={cardHead}><Icon name="search" size={14} />Spot check</p>
        {sc.status === 'TO_WALK' && !walk && (
          <div style={box}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={bluePill}>To walk</span><span style={muted}>picked for your walk{sc.pickedAt ? `, ${fmtWhen(sc.pickedAt)}` : ''}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <PrimaryButton busy={busy === 'walk'} onClick={() => recordWalk('PASSED')} style={smallBtn}>Passed</PrimaryButton>
              <SecondaryButton onClick={() => setWalk([{ type: '', note: '', photoIds: [], photos: [] }])} style={smallBtn}>Record findings</SecondaryButton>
            </div>
          </div>
        )}
        {sc.status === 'TO_WALK' && walk && (
          <div style={box}>
            {walk.map((f, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <Choice label={`Finding ${i + 1}`} options={FINDING_TYPES} value={f.type} onChange={(v) => setWalk((x) => x.map((y, j) => (j === i ? { ...y, type: v } : y)))} />
                <textarea value={f.note} rows={2} placeholder="What you found" aria-label={`What finding ${i + 1} is`} style={{ ...textBox, marginTop: 6 }}
                  onChange={(e) => { const v = e.target.value; setWalk((x) => x.map((y, j) => (j === i ? { ...y, note: v } : y))); }} />
                <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  {f.photos.map((p, k) => <Thumb key={p.id} photo={p} size={44} onOpen={() => openPhotos(f.photos, k)} onExpired={onExpired} />)}
                  <AddPhoto compact label="Add a photo" busy={busy === 'finding'} onFiles={async (files) => {
                    const sent = await sendPhotos('finding', files);
                    if (sent.length) setWalk((x) => x.map((y, j) => (j === i ? { ...y, photoIds: [...y.photoIds, ...sent.map((p) => p.id)], photos: [...y.photos, ...sent] } : y)));
                  }} />
                  {walk.length > 1 && <button type="button" onClick={() => setWalk((x) => x.filter((y, j) => j !== i))} style={{ ...linkBtn, color: 'var(--danger)', marginLeft: 'auto' }}>Remove</button>}
                </div>
              </div>
            ))}
            <button type="button" onClick={() => setWalk((x) => [...x, { type: '', note: '', photoIds: [], photos: [] }])} style={{ ...linkBtn, marginBottom: 10 }}>+ Another finding</button>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <PrimaryButton busy={busy === 'walk'} disabled={!walkReady} onClick={() => recordWalk('FINDINGS')} style={smallBtn}>Save findings</PrimaryButton>
              <SecondaryButton onClick={() => setWalk(null)} style={smallBtn}>Cancel</SecondaryButton>
              {!walkReady && <span style={muted}>Each finding needs its kind and a note</span>}
            </div>
          </div>
        )}
        {sc.status === 'PASSED' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={greenPill}><Icon name="check" size={12} />Passed</span><span style={muted}>walked by {whoOf(sc.walkedBy)}{sc.walkedAt ? `, ${fmtWhen(sc.walkedAt)}` : ''}</span>
          </div>
        )}
        {sc.status === 'FINDINGS' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <span style={redPill}>Findings</span><span style={muted}>walked by {whoOf(sc.walkedBy)}{sc.walkedAt ? `, ${fmtWhen(sc.walkedAt)}` : ''}</span>
            </div>
            {(sc.findings || []).map((f) => (
              <div key={f.id} style={{ ...box, marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={f.punch && f.punch.status === 'VERIFIED' ? grayPill : redPill}>{findingTypeOf(f.type)}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>Punch: {PUNCH[f.punch && f.punch.status] || 'Open'}</span>
                </div>
                {f.note && <div style={{ fontSize: 13.5, marginTop: 5, whiteSpace: 'pre-wrap' }}>{f.note}</div>}
                {f.photos && f.photos.length > 0 && <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>{f.photos.map((p, k) => <Thumb key={p.id} photo={p} size={44} onOpen={() => openPhotos(f.photos, k)} onExpired={onExpired} />)}</div>}
                <div style={{ ...muted, marginTop: 6 }}>
                  {f.punch && f.punch.status === 'OPEN' && 'Waiting on the sub'}
                  {f.punch && f.punch.status === 'FIXED' && `Done by ${whoOf(f.punch.fixedBy)}${f.punch.fixedAt ? `, ${fmtWhen(f.punch.fixedAt)}` : ''}`}
                  {f.punch && f.punch.status === 'VERIFIED' && `Verified by ${whoOf(f.punch.verifiedBy)}${f.punch.verifiedAt ? `, ${fmtWhen(f.punch.verifiedAt)}` : ''}`}
                </div>
                {f.punch && f.punch.status === 'FIXED' && (
                  <PrimaryButton busy={busy === `verify-${f.id}`} onClick={() => run(`verify-${f.id}`, () => verifyPunch(line.id, f.id, { requestId: newRequestId() }))} style={{ ...smallBtn, marginTop: 8 }}>Verify</PrimaryButton>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 10, alignItems: 'start' }}>
        {/* Requests and notes (2026-10-24, mockup approved): what the sub asked on this line -- material, can't get
            in, a count that differs, an issue -- and the notes, newest first, each answered here; E3's own buttons
            (Add a note, Raise an issue, Partly done) open one form at a time. Words as E3 Subs' line page. */}
        <div style={softCard}>
          <div style={{ ...cardHead, flexWrap: 'wrap', marginBottom: items.length || form ? 10 : 0 }}>
            <Icon name="inbox" size={14} />Requests and notes<span style={{ ...muted, fontWeight: 500 }}> · {openCount ? `${openCount} open` : 'none open'}</span>
            <span style={{ flex: 1 }} />
            <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
              {[['note', 'Add a note', 'pencil'], ['issue', 'Raise an issue', 'alert-triangle'], ...(canPartly ? [['partly', 'Partly done', 'clock']] : [])].map(([f, label, icon]) => (
                <SecondaryButton key={f} onClick={() => openForm(f)} aria-pressed={form === f}
                  style={{ ...smallBtn, fontWeight: 600, ...(form === f ? { background: 'var(--accent-soft)', borderColor: 'var(--accent)' } : {}) }}>
                  <Icon name={icon} size={13} />{label}
                  {f === 'partly' && installedNow > 0 && <span className="mono" style={{ fontSize: 12, marginLeft: 2 }}>{installedNow}/{qty}</span>}
                </SecondaryButton>
              ))}
            </span>
          </div>
          {form === 'note' && (
            <div style={{ ...box, marginBottom: 8 }}>
              <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value.slice(0, 4000))} rows={3} autoFocus
                placeholder="For the sub, e.g. the lift is on site Monday" aria-label="The note" style={textBox} />
              <div style={{ ...muted, margin: '6px 0 8px' }}>The sub sees it on the line. It emails nobody.</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <PrimaryButton busy={busy === 'note'} disabled={!noteDraft.trim()} onClick={addNote} style={smallBtn}>Add note</PrimaryButton>
                <SecondaryButton onClick={closeForm} style={smallBtn}>Cancel</SecondaryButton>
              </div>
            </div>
          )}
          {form === 'issue' && (
            <div style={{ ...box, marginBottom: 8 }}>
              <Choice label="What kind of issue" options={ISSUE_TYPES} value={issue.type} onChange={(v) => setIssue((x) => ({ ...x, type: v }))} />
              <textarea value={issue.note} onChange={(e) => { const v = e.target.value; setIssue((x) => ({ ...x, note: v })); }} rows={3}
                placeholder="What’s the issue?" aria-label="What the issue is" style={{ ...textBox, marginTop: 8 }} />
              <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {issue.photos.map((p, k) => <Thumb key={p.id} photo={p} size={44} onOpen={() => openPhotos(issue.photos, k)} onExpired={onExpired} />)}
                <AddPhoto compact label="Add a photo" busy={busy === 'issue-photo'} onFiles={async (files) => {
                  const sent = await sendPhotos('issue', files, 'issue-photo');
                  if (sent.length) setIssue((x) => ({ ...x, photoIds: [...x.photoIds, ...sent.map((p) => p.id)], photos: [...x.photos, ...sent] }));
                }} />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <PrimaryButton busy={busy === 'issue'} disabled={!issue.type || !issue.note.trim()} onClick={raise} style={smallBtn}>Raise an issue</PrimaryButton>
                <SecondaryButton onClick={closeForm} style={smallBtn}>Cancel</SecondaryButton>
              </div>
            </div>
          )}
          {form === 'partly' && (
            <div style={{ ...box, marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <label htmlFor={`partly-${line.id}`} style={{ fontSize: 13.5, color: 'var(--text-1)' }}>Installed so far</label>
                <input id={`partly-${line.id}`} type="number" min={0} max={Math.max(0, qty - 1)} step={1} value={partlyDraft} autoFocus
                  onChange={(e) => setPartlyDraft(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  onKeyDown={(e) => { if (e.key === 'Enter' && partlyOk) { e.preventDefault(); saveInstalled(); } }}
                  style={{ ...input, width: 90, height: 30 }} />
                <span style={{ fontSize: 13.5, color: 'var(--text-2)' }}>of {qty}</span>
                <PrimaryButton busy={busy === 'partly'} disabled={!partlyOk || partlyN === installedNow} onClick={saveInstalled} style={smallBtn}>Save</PrimaryButton>
                <SecondaryButton onClick={closeForm} style={smallBtn}>Cancel</SecondaryButton>
              </div>
              <div style={{ ...muted, marginTop: 6, color: partlyDraft !== '' && !partlyOk ? 'var(--warning)' : 'var(--text-3)' }}>
                {partlyDraft !== '' && partlyN >= qty ? `All ${qty} in? Use Mark complete.`
                  : 'Saves progress without completing the line. Mark complete still needs the after photo. 0 clears it.'}
              </div>
            </div>
          )}
          {items.map((x) => (x.issue
            ? <RequestCard key={x.key} issue={x.issue} busy={busy} onAnswer={(status, note, expectedAt) => answer(x.issue, status, note, expectedAt)} onOpenPhotos={openPhotos} onExpired={onExpired} />
            : <NoteCard key={x.key} note={x.note} />))}
        </div>
      </div>
      {viewing && <PhotoViewer list={viewing.list} index={viewing.index} onIndex={(i) => setViewing((v) => ({ ...v, index: i }))} onClose={() => setViewing(null)} />}
    </div>
  );
}
